import React, { ReactElement, useCallback, useState } from 'react';
import { Button, Checkbox, FadeAndSlideTransition } from '@framework-controls/core';
import * as css from './IndividualServicePanel.module.scss';
import MoreDetails from '../../MoreDetails/containers/MoreDetails';
import { strings } from 'src/localization';
import { Recom, WebOp, WebOpChecked, UpdateApptOpsInp, OpType, ApptOps, DeteremineDeptInp, ascCtaInteractionInput, VehicleRec,
    ascElementConfigurationInput, ApptVeh,
    SpecData} from 'src/models';
import { IconWrench } from 'src/content/icons';
import ToastBar from 'src/components/ToastBar/ToastBar';
import classNames from 'classnames';
import AdditionalServicePanelDetailMedia from 'src/components/AdditionalServices/AdditionalService/AdditionalServicePanel/AdditionalServicePanelDetail/AdditionalServicePanelDetailMedia/AdditionalServicePanelDetailMedia';
import { ascCtaInteraction, getElementColor, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import { sendSdEvent, getProviderId } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

export interface IProps {
    items: WebOp[];
    itemServiceChecked: WebOpChecked[];
    id: string;
    apptOps: ApptOps[];
    specDefaultDept: string;
    recom: Recom[];
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    theme: string;
    apptVeh: ApptVeh;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    hidePriceLabel?: string;
    toggleItem: (id: string) => void;
    UpdateApptOps: (inputOps: UpdateApptOpsInp[]) => void;
    determineDept: (inp: DeteremineDeptInp) => void;
    toggleRecommended: (id: string) => void;
}

const IndividualServicePanel = (props: IProps) => {
    const [toastFlag, setToastFlag] = useState<boolean>(false);
    const itemIndex = props.items.findIndex(result => result.id === props.id);
    let serviceImg: ReactElement | null = <IconWrench />;
    const imageIndex = props.items[itemIndex].urls.findIndex(result => result.mediaType === 'I');
    const SvcImgAltTag = props.items[itemIndex].webTitle + ' ' + strings.Image;
    const shouldHideStartingAt = props.theme === 'lexus' || props.theme === 'lexusMonogram';
    if (imageIndex !== -1 && props.items[itemIndex].urls[imageIndex].mediaUrls) {
        serviceImg = <img
            src={props.items[itemIndex].urls[imageIndex].mediaUrls}
            alt={SvcImgAltTag}
            className={css.serviceImg}>
        </img>;
    }

    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y';

    const serviceTitle = props.items[itemIndex].webTitle;
    let servicePrice: string | undefined = '';

    let toastBarDescription = '';
    let servicePriceLow = strings.CallDealerForPricing;
    if (props.items[itemIndex].itemCostLow !== '****') {
        servicePriceLow = '$' + (Number(props.items[itemIndex].itemCostLow) / 100).toFixed(2);
    }
    let servicePriceHigh = strings.CallDealerForPricing;
    if (props.items[itemIndex].itemCostHigh !== '****') {
        servicePriceHigh = '$' + (Number(props.items[itemIndex].itemCostHigh) / 100).toFixed(2);
    }
    if (props.items[itemIndex].itemCostHigh === '') {
        if (shouldHideStartingAt) {
            servicePrice = servicePriceLow;
        } else {
            servicePrice = strings.StartingAt + ' ' + servicePriceLow;
        }
    }
    if (props.items[itemIndex].itemCostLow === '') {
        servicePrice = strings.UpTo + ' ' + servicePriceHigh;
    }
    if ((props.items[itemIndex].itemCostLow !== '') && (props.items[itemIndex].itemCostHigh !== '')) {
        servicePrice = servicePriceLow + ' - ' + servicePriceHigh;
    }
    if (servicePriceHigh === servicePriceLow) {
        servicePrice = servicePriceHigh;
    }

    toastBarDescription = serviceTitle + ' - ' + servicePrice;


    if (servicePrice === strings.CallDealerForPricing && props.hidePriceLabel) {
        servicePrice = '';
        toastBarDescription = serviceTitle;
    }


    const rebuildOps = useCallback((serviceId) => {
        // Build the opsArray pulling in any ops from the service tab and then update the appointment state
        let opsArrayCopy: UpdateApptOpsInp[] = [...props.apptOps];

        const serviceChecked = props.itemServiceChecked.some(item => item.id === serviceId);

        // If a service is being checked (not currently showing as checked), push it to the ops list
        if (!serviceChecked) {
            const service = props.items.filter(item => item.id === serviceId);

            service.forEach(s => {
                opsArrayCopy.push(
                    {
                        id: 'individualService' + s.id,
                        operation: s.id,
                        concern: s.webTitle,
                        estTime: s.estTime,
                        menuID: '',
                        spgOp: s.spgOp,
                        opType: OpType.IndividualService,
                        itemCostLow: s.itemCostLow,
                        itemCostHigh: s.itemCostHigh,
                        department: s.department,
                        opWaiter: s.opWaiter,
                        opLoaner: s.opLoaner,
                        opRental: s.opRental,
                        opConcierge: s.opConcierge,
                        opCadillac: s.opCadillac,
                        opPPUDWL: s.opPPUDWL,
                    },
                );
            });
        } else {
            opsArrayCopy = opsArrayCopy.filter(item => item.operation !== serviceId);
        }

        props.UpdateApptOps(opsArrayCopy);
        const defaultDept = props.specDefaultDept;
        const determineDept = {
            defaultDept: defaultDept,
        };
        props.determineDept(determineDept);
    }, [
        props.apptOps,
        props.UpdateApptOps,
        props.determineDept,
        props.specDefaultDept,
        props.items,
        props.itemServiceChecked,
    ]);

    // define the onToggle method
    // Display the Service Added Toast bar
    const onToggle = (e: any, btnText: string) => {
        setToastFlag(true);
        props.toggleItem(props.id);
        rebuildOps(props.id);

        const checkedIndex = props.recom.findIndex(result => result.opCode === props.id);
        const itemChecked = props.itemServiceChecked.findIndex(result => result.id === props.id);
        if (checkedIndex !== -1) {
            props.toggleRecommended(props.id);
        }
        setTimeout(setToastFlag, 2000);
        const elementColor = getElementColor(e, 'fill');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: btnText ? btnText : serviceTitle,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'checkbox',
            eventAction: 'click',
            eventActionResult: itemChecked === -1 ? 'check' : 'uncheck',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: btnText ? btnText : serviceTitle,
            elementType: 'scheduling_tool',
            elementColor: elementColor,
            eventAction: 'click',
            eventActionResult: itemChecked === -1 ? 'check' : 'uncheck',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
        if (isSDAnalyticsEnabled) {
            sendSdEvent('scheduleServiceSelectionStart', {
                scheduleServiceSessionId: props.sessionId,
                scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                    props.lexusMCLaunched, props.lexusTDMCLaunched),
                scheduleServiceVehicleMake: props.apptVeh.make,
                scheduleServiceVehicleMileage: props.apptVeh.mileage,
                scheduleServiceVehicleModel: props.apptVeh.model,
                scheduleServiceVehicleVin: props.apptVeh.vin,
                scheduleServiceVehicleYear: props.apptVeh.vehYr,
            });
        }
    };

    // Set the variant style to be used by the checkbox control based on if it
    // is currently checked or not.
    let cbkClassName = 'primaryCkb';
    const checkedIndex = props.itemServiceChecked.findIndex(result => result.id === props.id);
    if (checkedIndex !== -1) {
        cbkClassName = 'primaryCkbChecked';
    }
    const classes = classNames(
        css.panel,
        {
            [css.selected]: checkedIndex !== -1,
        },
    );

    const whyBlock = props.items[itemIndex].why ?
        <div style={{ display: 'block' }}>
            <div className={css.detailsSalesTool}>
                {strings.WhyDoINeedThis}
            </div>
            <div className={css.detailsText}>
                {props.items[itemIndex].why}
            </div>
        </div> :
        null;

    const whatBlock = props.items[itemIndex].what ?
        <div style={{ display: 'block' }}>
            <div className={css.detailsSalesTool}>
                {strings.WhatIsThisFor}
            </div>
            <div className={css.detailsText}>
                {props.items[itemIndex].what}
            </div>
        </div> :
        null;

    const validMedia = props.items[itemIndex].urls.filter(
        result => result.mediaType === 'I' || result.mediaType === 'V');

    const mediaDisplay: JSX.Element[] | null = validMedia.map((panel, index) => <div
        key={itemIndex + panel.mediaUrls + panel.mediaType}
        className={css.mediaDisplay}
    >
        <AdditionalServicePanelDetailMedia
            key={index}
            id={panel.mediaType + index}
            opDesc={props.items[itemIndex].webTitle}
            opUrlType={panel.mediaType}
            opUrlLink={panel.mediaUrls}
            opUrlTrack={panel.trackUrls}
            vehicles={props.vehicles}
            selectedVehicle={props.selectedVehicle}
        />
    </div>);

    const detailsTitleStyles = classNames(
        css.detailsTitle,
        {
            [css.detailsTitleFord]: props.theme === 'ford',
            [css.detailsTitleIN]: props.theme === 'infiniti',
        },
    );

    const detailsPriceStyles = classNames(
        css.detailsPrice,
        {
            [css.detailsPriceFord]: props.theme === 'ford',
            [css.detailsPriceIN]: props.theme === 'infiniti',
        },
    );

    const detailsSalesToolStyles = classNames(
        css.detailsSalesTool,
        {
            [css.detailsSalesToolFord]: props.theme === 'ford',
            [css.detailsSalesToolIN]: props.theme === 'infiniti',
        },
    );

    let moreDetails = (props.items[itemIndex].why || props.items[itemIndex].what) ?
        <div style={{ display: 'block' }}>
            <MoreDetails
                headerLit={strings.OperationDetails}
                onAddService={onToggle}
                addServiceBtn={checkedIndex === -1}
                vehicles={props.vehicles}
                selectedVehicle={props.selectedVehicle}>
                <div
                    className={css.details}>
                    <div className={css.detailsTitle}>
                        {serviceTitle}
                    </div>
                    <div className={css.mediaDisplayArea}>
                        {mediaDisplay}
                    </div>
                    <div className={detailsPriceStyles}>
                        {servicePrice}
                    </div>
                    {whyBlock}
                    {whatBlock}
                </div>
            </MoreDetails>
        </div> :
        null;

    if (!moreDetails && props.items[itemIndex].complaintDef) {
        moreDetails =
        <div style={{ display: 'block' }}>
            <MoreDetails
                headerLit={strings.OperationDetails}
                onAddService={onToggle}
                addServiceBtn={checkedIndex === -1}
                vehicles={props.vehicles}
                selectedVehicle={props.selectedVehicle}>
                <div
                    className={css.details}>
                    <div className={detailsTitleStyles}>
                        {serviceTitle}
                    </div>
                    <div className={css.wrapper}>
                        <div className={css.hr}>
                            <hr />
                        </div>
                        <div className={detailsPriceStyles}>
                            {servicePrice}
                        </div>
                    </div>
                    <div style={{ display: 'block' }}>
                        <div className={detailsSalesToolStyles}>
                            {strings.WhatIsThis}
                        </div>
                        <div className={css.detailsText}>
                            {props.items[itemIndex].complaintDef}
                        </div>
                    </div>
                </div>
            </MoreDetails>
        </div>;
    }

    let CSSleftColumn = css.leftColumn;
    if (checkedIndex !== -1) {
        CSSleftColumn = css.leftColumnSelected;
    }

    const h4CardClass = classNames(
        css.h4Card,
        {
            [css.h4CardCV]: props.theme === 'chevrolet',
            [css.h4CardTextTransformNone]: props.theme === 'bmw' || props.theme === 'mercedesBenz',
            [css.h4CardIN]: props.theme === 'infiniti',
            [css.h4CardMidTextColor]: props.theme === 'nissan' || props.theme === 'mercedesBenz',
            [css.fontFamilyBaseFamily]: props.theme === 'mercedesBenz',
            [css.h4CardAcura]: props.theme === 'acura',
        },
    );

    const priceClass = classNames(
        css.price,
        {
            [css.priceSecondaryColor]: props.theme === 'acura-CA' || props.theme === 'chevrolet' ||
            props.theme === 'buick' || props.theme === 'bmw',
            [css.priceDefaultColor]: props.theme === 'mercedesBenz',
            [css.fontFamilyBaseFamily]: props.theme === 'mercedesBenz',
            [css.priceMazda]: props.theme === 'mazda',
            [css.priceAcura]: props.theme === 'acura',
            [css.priceGenesis]: props.theme === 'genesis',
        },
    );

    let h4CardId = 'h4Card';
    if (props.theme === 'acura-CA' || props.theme === 'chevrolet' || props.theme === 'honda' ||
        props.theme === 'buick' || props.theme === 'bmw' || props.theme === 'nissan') {
        h4CardId = 'h4CardSecondary';
    }

    return (
        <div className={classes}>
            <div className={css.checkBoxRow}>
                <div className={CSSleftColumn}>
                    <div style={{ height: '3rem', width: '3rem' }}>
                        {serviceImg}
                    </div>
                    <div className={priceClass}>
                        {servicePrice}
                    </div>
                </div>
                <div className={css.middleColumn}>
                    <div onClick={(e) => onToggle(e, '')}>
                        <h4 id={h4CardId} className={h4CardClass}>
                            {serviceTitle}
                        </h4>
                    </div>
                    {props.items[itemIndex].spgOp || props.items[itemIndex].complaintDef ? moreDetails : null}
                </div>
                <div className={css.rightColumn}>
                    <div className={css.checkBox}>
                        <Button id={'ItemCheckbtn'} variant='atext' tooltip={strings.AddSvcTip}
                            onClick={(e: any) => onToggle(e, '')}>
                            <Checkbox
                                id={'ItemCheckbox'}
                                size={'rg'}
                                variant={cbkClassName}
                                checked={checkedIndex !== -1}
                            >
                            </Checkbox>
                        </Button>
                        <FadeAndSlideTransition
                            in={toastFlag}
                            timeout={{
                                enter: 100,
                                exit: 100,
                            }}
                            isTop={checkedIndex !== -1}
                            zIndex={1000}>
                            <ToastBar
                                type='Service'
                                message={toastBarDescription}
                                theme={props.theme} />
                        </FadeAndSlideTransition>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default IndividualServicePanel;